import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Sheet } from '../../design/Sheet'
import { GymCover } from './GymCover'
import { isGymOpen } from './gymHours'
import { useMyMemberships } from './useMyMemberships'
import { useMyGyms } from './useMyGyms'
import type { GymBranding } from './useGymBranding'

interface Props {
  isOpen: boolean
  onClose: () => void
  /** The gym on screen now. */
  branding: GymBranding
  gymSlug: string
  occupancy?: number
}

/**
 * "Sălile tale", opened from the gym's logo at the top of Home.
 *
 * The gym you are looking at, as a big cover card that leads to its own page,
 * and your other gyms as cover tiles that switch to them. This is where the
 * cover photo the gym uploads lives, so Home itself stays a tool.
 */
export function GymsSheet({ isOpen, onClose, branding, gymSlug, occupancy = 0 }: Props) {
  const { t } = useTranslation()
  const { memberships, childAccount } = useMyMemberships()
  // One entry per gym: memberships has a row per plan, so a gym with a history
  // of plans would otherwise appear once for each.
  const { gyms } = useMyGyms()
  const isChild = childAccount !== null

  const others = gyms.filter((g) => g.slug !== gymSlug)
  const hereNow = memberships.some(
    (m) => m.gym.slug === gymSlug && m.subscription?.is_checked_in === true,
  )
  const hasHours = Boolean(branding.working_hours && branding.timezone)
  const open = hasHours && isGymOpen(branding.working_hours, branding.timezone)

  return (
    <Sheet isOpen={isOpen} onClose={onClose} aria-label={t('gymSpace.title')}>
      <div className="flex min-h-0 flex-col gap-3 overflow-y-auto overscroll-contain px-4 pb-6 pt-1">
        <h2 className="text-xl font-bold tracking-tight">{t('gymSpace.title')}</h2>

        <Link
          to={`/${gymSlug}/gym`}
          onClick={onClose}
          data-testid="current-gym-card"
          // shrink-0: in a scrolling column a long list of gyms squeezed this
          // card down to its border.
          className="shrink-0 overflow-hidden rounded-lg border-2 border-[var(--gym-primary)] bg-content1 shadow-e1"
        >
          <GymCover
            name={branding.name}
            coverUrl={branding.cover_url}
            color={branding.primary_color}
            className="h-28"
          >
            <span className="absolute bottom-2.5 left-3 right-3 truncate text-[17px] font-bold text-white">
              {branding.name}
            </span>
            {hereNow && (
              <span className="absolute right-2.5 top-2.5 rounded-full bg-white px-2.5 py-0.5 text-xs font-bold text-foreground">
                {t('gymSpace.hereNow')}
              </span>
            )}
          </GymCover>
          <span className="flex items-center gap-2 px-3 py-2.5 text-sm">
            {hasHours && (
              <span className={`font-semibold ${open ? 'text-success' : 'text-danger'}`}>
                ● {open ? t('gymHours.openNow') : t('gymHours.closed')}
              </span>
            )}
            {open && occupancy > 3 && (
              <span className="truncate text-foreground/50">
                · {t('gymHours.currentlyInGym', { count: occupancy })}
              </span>
            )}
            <span className="ml-auto shrink-0 font-semibold text-[var(--gym-primary)]">
              {t('gymSpace.details')} ›
            </span>
          </span>
        </Link>

        {others.length > 0 && (
          <div className="grid shrink-0 grid-cols-2 gap-2.5">
            {others.map((gym) => (
              <Link
                key={gym.id}
                to={isChild ? `/${gym.slug}/bouldering/routes` : `/${gym.slug}/dashboard`}
                onClick={onClose}
                data-testid="other-gym"
                className="overflow-hidden rounded-lg border border-[var(--gym-line)] bg-content1 shadow-e1"
              >
                <GymCover name={gym.name} coverUrl={gym.cover_url} className="h-16" />
                <span className="block truncate px-2.5 py-2 text-sm font-semibold">{gym.name}</span>
              </Link>
            ))}
          </div>
        )}

        {!isChild && (
          <Link
            to="/our-gyms"
            onClick={onClose}
            className="shrink-0 self-center py-1 text-sm font-semibold text-[var(--gym-primary)]"
          >
            + {t('gymSpace.findGym')}
          </Link>
        )}
      </div>
    </Sheet>
  )
}
